import * as THREE from "three";

import {
  CHUNK_SIZE,
  type ChunkCoord,
  type ChunkMeshBuffers,
  type ChunkMeshUpdate,
  validateChunkMeshBuffers,
} from "../world/types";

/*
 * The chunk-mesh registry: the world's slab buffers in, one Three.js mesh per
 * chunk *column* out.
 *
 * The registry is the only place in the client that knows both the buffer
 * contract and Three.js, and it is deliberately dumb — it uploads what it is
 * given and it never asks where the geometry came from.
 *
 * # Why a column and not a chunk
 *
 * A chunk is 16 x 16 x **1** (map §13.1), which is a poor geometry-to-draw-call
 * ratio: one mesh per chunk means one draw call per z-level per column, and the
 * measurement said so. A headless walk on this machine, radius-4 interest, 931
 * slab meshes in the scene: **389–503 draw calls, mean 445** — right at the
 * ceiling the rendering research puts on a single-material scene ("~400 calls
 * with a single program is comfortably fine on desktop", but measure —
 * `docs/research/rendering/threejs-chunks.md` §1). The same brief names the fix
 * and its order: *"the cheapest fix isn't batching — it's footprint … baking a
 * 16x16xN column of slabs into one render geometry quarters-or-better the call
 * count with no new machinery. Do this before `BatchedMesh`."*
 *
 * So the registry keeps every slab's buffers and concatenates the ones in a
 * column into a single geometry. Index offsetting is the whole of the merge,
 * and it is mechanical because the mesher emits **world-space** positions
 * ({@link ChunkMeshBuffers}) — there is no per-slab transform to reconcile. The
 * same walk after the change draws 40–70 calls.
 *
 * The cost is paid in three places, and all three are cheap:
 *
 * - **Memory.** Every slab's buffers stay in JavaScript; a full window is about
 *   2.5 MB. They are already copies (see below), so this is a reference kept,
 *   not a copy made.
 * - **Rebuilds.** A slab arriving dirties its column, and a dirty column is
 *   rebuilt whole. Slabs stream in nearest-first, so a column's slabs mostly
 *   arrive together and the dirty set coalesces them into one rebuild per frame.
 * - **Culling granularity.** A column is a 16 x 16 x N box, so frustum culling
 *   is coarser than it was per slab. That is the trade the draw-call number
 *   bought, and the streaming window is the primary visibility filter anyway
 *   (research §3).
 *
 * # Buffers are copied, always
 *
 * One invariant is worth stating loudly, because getting it wrong produces a bug
 * that looks like a graphics driver problem: **nothing here may hold a view into
 * wasm linear memory.** Those views detach without warning when the sim's heap
 * grows (rust guide §14: "re-create views after every tick, never cache one
 * across calls"). The buffers arriving here are already owned copies — every
 * `ChunkMesh` accessor allocates a fresh typed array (`crates/sim-wasm`'s
 * `mesh` module) — so the registry stores them directly and copies again only
 * where a `BufferAttribute` needs a contiguous array of its own.
 */

/**
 * Columns rebuilt and uploaded in one call to {@link ChunkMeshRegistry.apply}.
 *
 * The rendering research caps new geometries at "~2–4 per frame" because
 * `bufferData` stalls (§2). Three, and whatever does not fit waits for the next
 * frame in the order it was dirtied.
 */
export const MAX_UPLOADS_PER_FRAME = 3;

/** One column's slabs, by z, and the mesh they are drawn as. */
interface Column {
  readonly cx: number;
  readonly cy: number;

  /** The slab buffers, keyed by z-level. Insertion order; rendering does not care. */
  readonly slabs: Map<number, ChunkMeshBuffers>;

  /** The mesh in the scene, or `null` until the column has been built once. */
  mesh: THREE.Mesh | null;
}

export class ChunkMeshRegistry {
  private readonly scene: THREE.Scene;
  private readonly material: THREE.Material;
  private readonly columns = new Map<string, Column>();

  /** Columns whose geometry no longer matches their slabs, in the order they went stale. */
  private readonly dirty = new Set<string>();

  /** Slabs with geometry, across every column. What the HUD calls "chunks". */
  private slabCount = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    // One material for every column: colour comes from the vertex stream, so
    // there is nothing per-chunk to vary and sharing keeps the draw calls
    // batchable. Lambert rather than Standard — the cut list has no PBR in it,
    // and flat-shaded voxels do not benefit from roughness.
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
  }

  /** How many chunk slabs currently have geometry in the scene. */
  get size(): number {
    return this.slabCount;
  }

  /** How many columns those slabs are drawn as — the draw calls before culling. */
  get columnCount(): number {
    return this.columns.size;
  }

  /** How many columns are waiting to be rebuilt. */
  get pendingUploads(): number {
    return this.dirty.size;
  }

  /**
   * Ingests a batch of updates and rebuilds at most
   * {@link MAX_UPLOADS_PER_FRAME} columns.
   *
   * Ingesting is cheap — a map write and a dirty mark — so the whole batch is
   * taken every frame however large it is; only the uploads are budgeted. That
   * split is what makes the budget meaningful: a hundred slabs arriving in one
   * frame is a handful of columns, not a hundred geometries.
   */
  apply(updates: readonly ChunkMeshUpdate[]): void {
    for (const update of updates) {
      if (update.kind === "set") {
        this.setChunkMesh(update.coord, update.buffers);
      } else {
        this.removeChunkMesh(update.coord);
      }
    }

    let budget = MAX_UPLOADS_PER_FRAME;

    for (const key of this.dirty) {
      if (budget === 0) {
        break;
      }

      this.dirty.delete(key);
      this.rebuild(key);
      budget -= 1;
    }
  }

  /** Records one slab's buffers. The column they belong to is rebuilt later. */
  setChunkMesh(coord: ChunkCoord, buffers: ChunkMeshBuffers): void {
    // Checked rather than trusted: the far side of this contract is generated
    // Rust behind a wasm ABI, and a layout drift should name the chunk rather
    // than draw garbage ({@link validateChunkMeshBuffers}).
    validateChunkMeshBuffers(coord, buffers);

    const key = columnKey(coord);
    const column = this.columns.get(key) ?? {
      cx: coord.cx,
      cy: coord.cy,
      slabs: new Map<number, ChunkMeshBuffers>(),
      mesh: null,
    };

    if (!column.slabs.has(coord.z)) {
      this.slabCount += 1;
    }

    column.slabs.set(coord.z, buffers);
    this.columns.set(key, column);
    this.dirty.add(key);
  }

  /** Drops one slab. Silent if there was none. */
  removeChunkMesh(coord: ChunkCoord): void {
    const key = columnKey(coord);
    const column = this.columns.get(key);

    if (column === undefined || !column.slabs.delete(coord.z)) {
      return;
    }

    this.slabCount -= 1;
    this.dirty.add(key);
  }

  /** Drops everything. The shared material outlives it — this registry owns it. */
  clear(): void {
    for (const column of this.columns.values()) {
      this.discardMesh(column);
    }

    this.columns.clear();
    this.dirty.clear();
    this.slabCount = 0;
  }

  /** Releases the shared material too. The registry is unusable afterwards. */
  dispose(): void {
    this.clear();
    this.material.dispose();
  }

  /**
   * Rebuilds one column's geometry from its slabs.
   *
   * Recreate-and-dispose rather than writing into the old attributes: under
   * `WebGLRenderer`, replacing a `BufferAttribute` on a live geometry orphans
   * the GPU buffer behind it — the renderer's attribute cache is keyed by the
   * attribute object and never deletes the one it replaced
   * (`docs/research/rendering/threejs-chunks.md` §1, three.js #26835). The
   * research calls this "the sharp pitfall"; it is the reason this method builds
   * a whole new geometry every time.
   */
  private rebuild(key: string): void {
    const column = this.columns.get(key);

    if (column === undefined) {
      return;
    }

    if (column.slabs.size === 0) {
      // Dug out entirely, or streamed away. The column goes with it rather than
      // lingering as an empty mesh nobody draws.
      this.discardMesh(column);
      this.columns.delete(key);

      return;
    }

    let vertexCount = 0;
    let indexCount = 0;

    for (const slab of column.slabs.values()) {
      vertexCount += slab.positions.length / 3;
      indexCount += slab.indices.length;
    }

    const positions = new Float32Array(vertexCount * 3);
    const normals = new Float32Array(vertexCount * 3);
    const colors = new Float32Array(vertexCount * 3);
    const indices = new Uint32Array(indexCount);

    let vertexOffset = 0;
    let indexOffset = 0;
    let lowestZ = Number.POSITIVE_INFINITY;
    let highestZ = Number.NEGATIVE_INFINITY;

    for (const [z, slab] of column.slabs) {
      positions.set(slab.positions, vertexOffset * 3);
      normals.set(slab.normals, vertexOffset * 3);
      colors.set(slab.colors, vertexOffset * 3);

      // The only arithmetic in the merge: every index moves by the number of
      // vertices already written. Positions are world-space, so nothing else
      // has to be reconciled.
      for (let i = 0; i < slab.indices.length; i += 1) {
        indices[indexOffset + i] = (slab.indices[i] ?? 0) + vertexOffset;
      }

      vertexOffset += slab.positions.length / 3;
      indexOffset += slab.indices.length;
      lowestZ = Math.min(lowestZ, z);
      highestZ = Math.max(highestZ, z);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    // Set from the column's extents rather than computed from the vertices:
    // `computeBoundingSphere()` is an O(n) pass over every position on every
    // rebuild, and the answer is already known — a column is a box of known size
    // in a known place (research §3).
    const halfHeight = (highestZ + 1 - lowestZ) / 2;
    geometry.boundingSphere = new THREE.Sphere(
      new THREE.Vector3(
        column.cx * CHUNK_SIZE + CHUNK_SIZE / 2,
        column.cy * CHUNK_SIZE + CHUNK_SIZE / 2,
        lowestZ + halfHeight,
      ),
      Math.hypot(CHUNK_SIZE / 2, CHUNK_SIZE / 2, halfHeight),
    );

    if (column.mesh === null) {
      const mesh = new THREE.Mesh(geometry, this.material);
      mesh.name = `column ${column.cx},${column.cy}`;
      // Geometry is emitted in world coordinates (see `ChunkMeshBuffers`), so
      // the object stays at the origin and the matrix stays identity.
      mesh.matrixAutoUpdate = false;
      column.mesh = mesh;
      this.scene.add(mesh);

      return;
    }

    column.mesh.geometry.dispose();
    column.mesh.geometry = geometry;
  }

  /** Removes a column's mesh from the scene and frees its GPU buffers. */
  private discardMesh(column: Column): void {
    if (column.mesh === null) {
      return;
    }

    this.scene.remove(column.mesh);
    column.mesh.geometry.dispose();
    column.mesh = null;
  }
}

/**
 * The key a column is held under: a chunk coordinate with the z dropped.
 *
 * Deliberately not `chunkKey` with a zeroed z, which would read as a chunk that
 * happens to be at ground level in a debugger.
 */
function columnKey(coord: ChunkCoord): string {
  return `${coord.cx},${coord.cy}`;
}
